import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Wrench, Play } from "@/components/icons";
import { DeleteConfirm } from "@/components/DeleteConfirm";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { plural } from "@/lib/plural";
import { ErrorState } from "@/components/ListState";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const emptyForm = {
  name: "", scope_type: "category", category_id: "", location_id: "__all__",
  asset_id: "", interval_type: "months", interval_value: "3", checklist: "", assigned_role: "",
};

export const SettingsMaintenance = () => {
  const { canWrite, canView } = useAuth();
  const [schedules, setSchedules] = useState([]);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      // /maintenance-schedules sits behind the `kalender` module guard. Asking
      // for it unconditionally turned a 403 into a redirect that threw a
      // management user out of Instellingen entirely — including the tabs the
      // owner deliberately left visible.
      const canSchedules = canView("kalender");
      const [s, c, l, a] = await Promise.all([
        canSchedules ? api.get("/maintenance-schedules") : Promise.resolve({ data: [] }),
        api.get("/asset-categories"),
        api.get("/locations"), api.get("/assets"),
      ]);
      setSchedules(s.data); setCategories(c.data); setLocations(l.data); setAssets(a.data);
    } catch (e) { setLoadError(getErrorMessage(e, "Kan schema's niet laden.")); }
    finally { setLoading(false); }
  }, [canView]);
  useEffect(() => { load(); }, [load]);

  // Show which assets a scope covers BEFORE saving.
  const runPreview = async (f) => {
    try {
      const body = f.scope_type === "asset"
        ? { scope_type: "asset", asset_id: f.asset_id || null }
        : { scope_type: "category", category_id: f.category_id || null, location_id: f.location_id === "__all__" ? null : f.location_id };
      if ((f.scope_type === "asset" && !f.asset_id) || (f.scope_type === "category" && !f.category_id)) { setPreview(null); return; }
      const { data } = await api.post("/maintenance-schedules/preview-scope", body);
      setPreview(data);
    } catch (e) { setPreview(null); }
  };
  useEffect(() => { if (open) runPreview(form); /* eslint-disable-next-line */ }, [form.scope_type, form.category_id, form.location_id, form.asset_id, open]);

  const submit = async () => {
    if (!form.name) { toast.error("Naam is verplicht."); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        scope_type: form.scope_type,
        category_id: form.scope_type === "category" ? form.category_id : null,
        location_id: form.scope_type === "category" && form.location_id !== "__all__" ? form.location_id : null,
        asset_id: form.scope_type === "asset" ? form.asset_id : null,
        interval_type: form.interval_type,
        interval_value: Number(form.interval_value) || 1,
        checklist: form.checklist.split("\n").map((s) => s.trim()).filter(Boolean),
        assigned_role: form.assigned_role || null,
        active: true,
      };
      await api.post("/maintenance-schedules", payload);
      toast.success("Onderhoudsschema opgeslagen");
      setOpen(false); setForm(emptyForm); setPreview(null);
      await load();
    } catch (e) { toast.error(getErrorMessage(e, "Opslaan mislukt.")); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/maintenance-schedules/${id}`); toast.success("Schema verwijderd"); await load(); }
    catch (e) { toast.error(getErrorMessage(e, "Verwijderen mislukt.")); }
  };
  const generate = async () => {
    try { const { data } = await api.post("/maintenance-schedules/generate"); toast.success(`${plural(data.created, "taak", "taken")} gegenereerd`); }
    catch (e) { toast.error(getErrorMessage(e, "Genereren mislukt.")); }
  };

  const catName = (id) => categories.find((c) => c.category_id === id)?.name || id;

  return (
    <div>
      <div className="mb-3 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-body text-muted-foreground">Preventief onderhoud dat automatisch onderhoudstaken in de kalender genereert.</p>
        {canWrite() && (
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="maintenance-generate-button" onClick={generate}><Play size={18} className="mr-2" /> Nu genereren</Button>
            <Button size="sm" data-testid="maintenance-add-button" onClick={() => { setForm(emptyForm); setPreview(null); setOpen(true); }}><Plus size={18} className="mr-2" /> Nieuw schema</Button>
          </div>
        )}
      </div>

      {loadError ? (
        <ErrorState testid="maintenance-load-error" message={loadError} onRetry={load} />
      ) : loading ? <p className="text-body text-muted-foreground">Laden...</p> : schedules.length === 0 ? (
        <Card className="p-6 text-center text-body text-muted-foreground" data-testid="maintenance-empty">Nog geen onderhoudsschema's.</Card>
      ) : (
        <div className="space-y-2" data-testid="maintenance-list">
          {schedules.map((s) => (
            <Card key={s.id} className="p-4" data-testid={`maintenance-row-${s.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2">
                    <Wrench size={18} className="text-letop-tekst" />
                    <span className="font-medium text-foreground">{s.name}</span>
                    {!s.active && <Badge variant="secondary">Inactief</Badge>}
                  </div>
                  <div className="mt-1 text-data text-muted-foreground">
                    {s.scope_type === "asset" ? "1 asset" : `Categorie: ${catName(s.category_id)}${s.location_id ? ` · locatie ${s.location_id}` : " · alle locaties"}`}
                    {" · "}Interval: {s.interval_value} {s.interval_type === "months" ? "maanden" : "gebruikseenheden"}
                    {" · "}Dekt {plural(s.covered_count, "asset", "assets")}
                  </div>
                </div>
                {canWrite() && <DeleteConfirm
                  trigger={<Button size="icon" variant="ghost" data-testid={`schedule-delete-${s.id}`}><Trash2 size={18} /></Button>}
                  title="Onderhoudsschema verwijderen?"
                  description="Dit kan niet ongedaan gemaakt worden."
                  onConfirm={() => remove(s.id)}
                  testid={`schedule-delete-confirm-${s.id}`}
                />}
              </div>
              {s.checklist?.length > 0 && (
                <ol className="mt-2 list-inside list-decimal text-data text-muted-foreground">{s.checklist.map((c, i) => <li key={i}>{c}</li>)}</ol>
              )}
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" srDescription="Stel het onderhoudsschema in: scope, interval en eventuele checklist.">
          <DialogHeader><DialogTitle>Onderhoudsschema</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <div className="space-y-1.5"><Label>Naam *</Label><Input data-testid="maintenance-name-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Scope</Label>
                <Select value={form.scope_type} onValueChange={(v) => setForm((f) => ({ ...f, scope_type: v, interval_type: v === "category" ? "months" : f.interval_type }))}>
                  <SelectTrigger data-testid="maintenance-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="category">Categorie</SelectItem><SelectItem value="asset">Eén asset</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Interval-type</Label>
                <Select value={form.interval_type} onValueChange={(v) => setForm((f) => ({ ...f, interval_type: v }))} disabled={form.scope_type === "category"}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="months">Maanden</SelectItem><SelectItem value="usage">Gebruik</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            {form.scope_type === "category" ? (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Categorie</Label>
                  <Select value={form.category_id} onValueChange={(v) => setForm((f) => ({ ...f, category_id: v }))}>
                    <SelectTrigger data-testid="maintenance-category-select"><SelectValue placeholder="Kies" /></SelectTrigger>
                    <SelectContent>{categories.map((c) => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Locatie (optioneel)</Label>
                  <Select value={form.location_id} onValueChange={(v) => setForm((f) => ({ ...f, location_id: v }))}>
                    <SelectTrigger data-testid="maintenance-location-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="__all__">Alle locaties</SelectItem>{locations.map((l) => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>Asset</Label>
                <Select value={form.asset_id} onValueChange={(v) => setForm((f) => ({ ...f, asset_id: v }))}>
                  <SelectTrigger data-testid="maintenance-asset-select"><SelectValue placeholder="Kies asset" /></SelectTrigger>
                  <SelectContent>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_code} — {a.brand} {a.model}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-1.5"><Label>Interval ({form.interval_type === "months" ? "maanden" : "gebruikseenheden"})</Label><Input type="number" min="1" data-testid="maintenance-interval-input" value={form.interval_value} onChange={(e) => setForm((f) => ({ ...f, interval_value: e.target.value }))} /></div>
            <div className="space-y-1.5"><Label>Checklist (één stap per regel)</Label><Textarea data-testid="maintenance-checklist-input" rows={4} value={form.checklist} onChange={(e) => setForm((f) => ({ ...f, checklist: e.target.value }))} placeholder={"Controleer slijtage\nControleer bevestiging"} /></div>

            {/* Scope preview so nobody accidentally schedules across all 19 locations */}
            {preview && (
              <div className="rounded-md border border-border bg-muted/30 p-3" data-testid="maintenance-scope-preview">
                <div className="text-body font-medium text-foreground">Dit schema dekt {plural(preview.count, "asset", "assets")}:</div>
                <div className="mt-1 max-h-24 overflow-auto text-data text-muted-foreground">
                  {preview.assets.slice(0, 30).map((a) => <div key={a.id}>{a.asset_code} — {a.label} ({a.location_id || "geen locatie"})</div>)}
                  {preview.count === 0 && <div>Geen assets — controleer de scope.</div>}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button data-testid="maintenance-save-button" onClick={submit} disabled={saving}>{saving ? "Bezig..." : "Opslaan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
